﻿import { ov } from "./pilotgaea-web";
import { OpenLRDecoder } from "./openLRDecoder";
import { TDXCityInfo } from "./tdxCityInfo";

/** */
export type TrackWidgetSetting = Required<ov.Widget.WidgetSetting> & {
  /**
   * 代理伺服器，可給代理如PROXY_URL/{URL}。
   * @defaultValue ""
   */
  proxy?: string;
  /** 時間軸物件，需有設定將會自動將歷史回波加入時間軸。 */
  timeline?: ov.Widget.Timeline;
  /**
   * 是否在關閉Widget時清除所有軌跡。
   * @defaultValue true
   */
  clearOnClose?: boolean;
  eTagCities?: string[];
};

export interface TDXApiInfo  {
  /** API類型 */
  type: string;
  /** API名稱 */
  name: string;
  /** API簡稱 */
  shortName: string;
  /** API路徑 */
  url: string;
};

export type TDXeTagEntity = {
  layer?: ov.CustomLayer;
  pairData?: any;
  pairDataUpdated?: boolean;
}

export type TDXCert = {
  client_id: string;
  client_secret: string;
  grant_type: string;
  token?: {
    access_token: string;
    expires_in: number;
    refresh_expires_in: number;
    token_type: string;
    "not-before-policy": number;
    scope: string;
    timestamp: number;
  };
};

/**
 * 軌跡Widget
 *
 * 需加入`material-icons.css`
 * ```html
 * <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
 * ```
 * 或參照 {@link https://developers.google.com/fonts/docs/material_icons | Google Fonts Material Icons Guide} 設置離線版本。
 */
export class TrackWidget extends ov.Widget.BaseWidget<ov.UI.WindowUI> {
  _TerrainView: ov.TerrainView; // 要跟view拿模組
  _Proxy: string;
  _APIServer: string;
  _TDXServer: string;
  _TDXApi: TDXApiInfo[];
  _TDXCert?: TDXCert;
  _Timeline: null | ov.Widget.Timeline;
  _TrackCount: number;
  _ClearOnClose: boolean;
  _ResultContainer!: HTMLDivElement;
  _ApiContainer!: HTMLDivElement;
  _FileContainer!: HTMLDivElement;
  _TdxContainer!: HTMLDivElement;
  _ResultTabRadio!: HTMLInputElement;
  _APIKeyInput!: HTMLInputElement;
  _FlightIdInput!: HTMLInputElement;
  _OkBtn!: HTMLButtonElement;
  _SelectType!: HTMLSelectElement;
  _FileInput!: HTMLInputElement;
  _UploadBtn!: HTMLButtonElement;
  _TdxApiIdInput!: HTMLInputElement;
  _TdxApiSecretInput!: HTMLInputElement;
  _TdxSelectType!: HTMLSelectElement;
  _TdxOkBtn!: HTMLButtonElement;  
  _HighwayLayer: ov.TerrainCustomTileOverlay | null = null;
  _HighwayLegend?: HTMLElement;
  _HighwaySectionData?: any;
  _HighwaySectionDataUpdated?: boolean;
  _ETagEntities: { [key: string]: TDXeTagEntity } = {};
  _EtagHsinchuLayer?: ov.CustomLayer;
  _EtagHsinchuCountyLayer?: ov.CustomLayer;
  _EtagPairsDataHsinchu?: any;
  _EtagPairsDataHsinchuUpdated?: boolean;
  _EtagPairsDataHsinchuCounty?: any;
  _EtagPairsDataHsinchuCountyUpdated?: boolean;
  _EtagLegend?: HTMLElement;


  /**
   * 軌跡Widget
   * @param param 參數。
   */
  constructor(param: TrackWidgetSetting) {
    super(param);
    this._TerrainView = param.view;
    this._Proxy = "";
    this._APIServer = "https://aeroapi.flightaware.com/aeroapi/";
    this._Timeline = null;
    this._TrackCount = 1;
    this._ClearOnClose = true;

    //#region TDX init
    this._TDXServer = "https://tdx.transportdata.tw/api/basic";
    this._TDXApi = [
      {
        type: "highway",
        name: "高速公路發布路段即時路況資料",
        shortName: "高速公路即時路況",
        url: "/v2/Road/Traffic/Live/Freeway",
      }
    ];

    if (param.eTagCities) {
      const cities = param.eTagCities;
      // 檢查是否為字串陣列
      if (cities.every((city) => typeof city === "string")) {
        this._TDXApi.push(
          ...cities
            .map((city) => {
              const cityInfo = TDXCityInfo.getCityInfoByCityNameEng(city);
              if (cityInfo) {
                return {
                  type: `etag${cityInfo.CityNameEng}`,
                  name: `${cityInfo.CityName}電子標籤門架配對路段即時路況`,
                  shortName: `${cityInfo.CityName}ETag即時路況`,
                  url: `/v2/Road/Traffic/Live/ETag/City/${cityInfo.CityNameEng}`,
                };
              }
            })
            .filter((item) => item !== undefined)
        );
        
      }else{
        console.error("建立 track widget 使用的 eTagCities 參數錯誤，請檢查是否為字串陣列");
      }
    }else{
      // 使用預設值
      this._TDXApi.push(
        {
          type: "etagHsinchu",
          name: "新竹市電子標籤門架配對路段即時路況",
          shortName: "新竹市ETag即時路況",
          url: "/v2/Road/Traffic/Live/ETag/City/HsinchuCounty",
        },
        {
          type: "etagHsinchuCounty",
          name: "新竹縣電子標籤門架配對路段即時路況",
          shortName: "新竹縣ETag即時路況",
          url: "/v2/Road/Traffic/Live/ETag/City/Hsinchu",
        }
      )
    }

    //#endregion TDX init

    this._updateParameter(param);
    this._createUI();
    this._initEvent();
    this._appendToCanvas();
    this._init();
  }

  _beforeClose() {
    if (this._ClearOnClose) {
      const results = this._ResultContainer.querySelectorAll<HTMLElement>(
        ".ov-widget-deleteBtn"
      );
      for (let i = 0; i < results.length; i++) {
        results[i].click();
      }
    }
  }

  _updateParameter(parameter: TrackWidgetSetting) {
    if (!parameter) return;
    const { proxy, timeline, clearOnClose } = parameter;
    this._Proxy = typeof proxy === "string" ? proxy : this._Proxy;
    this._Timeline = timeline ?? this._Timeline;
    this._ClearOnClose =
      typeof clearOnClose === "boolean" ? clearOnClose : this._ClearOnClose;
  }

  _createUI() {
    let x = 0;
    if (this._TerrainView) {
      x = this._TerrainView.canvas.width - 450;
    }
    this._WidgetUI = new ov.UI.WindowUI({
      x: x,
      y: 50,
      title: "軌跡管理",
      closeButton: true,
      minimizeButton: true,
      draggable: true,
      onClose: this._onClose.bind(this),
    });
    this._WidgetUI.getElement().classList.add("ov-widget-trackWidget");
    this._WidgetUI.getElement().classList.add("ov-widget-unselectable");
    this._WidgetUI.getElement().classList.add("ov-widget-hideScrollBar");
    this._WidgetUI.getTitleElement().classList.add("ov-widget-header");
    this.divider = document.createElement("div");
    this.divider.classList.add("ov-widget-divider");

    this._ApiContainer = document.createElement("div");
    this._ApiContainer.classList.add("ov-widget-tabs-content");
    this._ApiContainer.classList.add("ov-widget-hideScrollBar");
    this._FileContainer = this._ApiContainer.cloneNode(true) as HTMLDivElement;
    this._TdxContainer = this._ApiContainer.cloneNode(true) as HTMLDivElement;
    const resultContainer = this._ApiContainer.cloneNode(
      true
    ) as HTMLDivElement;
    this._ResultContainer = document.createElement("div");
    this._ResultContainer.classList.add("ov-widget-scrollBar");
    this._ResultContainer.style.overflowY = "auto";
    this._ResultContainer.style.height = "100%";
    resultContainer.append(this._ResultContainer);
    const radio = document.createElement("input");
    const label = document.createElement("label");
    const t = Math.ceil(new Date().getTime() * Math.random());
    radio.classList.add("ov-widget-tabs-radio");
    label.classList.add("ov-widget-tabs-label");
    radio.name = "DTM" + t;
    radio.type = "radio";
    const radio2 = radio.cloneNode(true) as HTMLInputElement;
    const radio3 = radio.cloneNode(true) as HTMLInputElement;
    this._ResultTabRadio = radio.cloneNode(true) as HTMLInputElement;
    const label2 = label.cloneNode(true) as HTMLLabelElement;
    const label3 = label.cloneNode(true) as HTMLLabelElement;
    const label4 = label.cloneNode(true) as HTMLLabelElement;
    radio.checked = true;
    radio.id = "apiTab" + t;
    radio2.id = "fileTab" + t;
    radio3.id = "tdxTab" + t;
    this._ResultTabRadio.id = "resultTab" + t;
    label.classList.add("ov-widget-tabs-labelFirst");
    label.innerText = "API輸入";
    label2.innerText = "上傳檔案";
    label3.innerText = "串接TDX";
    label4.classList.add("ov-widget-tabs-labelLast");
    label4.innerText = "清單";
    label.setAttribute("for", radio.id);
    label2.setAttribute("for", radio2.id);
    label3.setAttribute("for", radio3.id);
    label4.setAttribute("for", this._ResultTabRadio.id);

    this._APIKeyInput = document.createElement("input");
    this._APIKeyInput.setAttribute("type", "text");
    this._APIKeyInput.setAttribute("id", "widget-track-tdx-flightaware-key");
    this._APIKeyInput.placeholder = "請至 Flightaware 官網註冊取得API Key";    
    this._InsertContent("API Key", this._APIKeyInput, this._ApiContainer);

    this._FlightIdInput = document.createElement("input");
    this._FlightIdInput.setAttribute("type", "text");
	this._FlightIdInput.setAttribute("id", "widget-track-tdx-flightaware-id");
    this._FlightIdInput.placeholder = "請輸入飛航ID";    
    this._InsertContent("ID", this._FlightIdInput, this._ApiContainer);

    this._OkBtn = document.createElement("button");
    this._OkBtn.classList.add("ov-widget-btn");
    this._OkBtn.innerText = "新增";
    this._ApiContainer.append(this._OkBtn);

    this._SelectType = document.createElement("select");
    this._SelectType.classList.add("ov-widget-select");
    ["GPX", "FlightAware", "FlightRadarCSV", "AIS"].forEach((value) => {
      const op = document.createElement("option");
      op.text = value;
      this._SelectType.append(op);
    });

    this._FileInput = document.createElement("input");
    this._FileInput.type = "file";

    this._UploadBtn = document.createElement("button");
    this._UploadBtn.classList.add("ov-widget-btn");
    this._UploadBtn.innerText = "確認上傳";
    this._FileContainer.append(this._SelectType);
    this._FileContainer.append(this.divider.cloneNode(true));
    this._FileContainer.append(this._FileInput);
    this._FileContainer.append(this.divider.cloneNode(true));
    this._FileContainer.append(this._UploadBtn);

    //#region TDX Tab

    this._TdxApiIdInput = document.createElement("input");
    this._TdxApiIdInput.setAttribute("type", "text");
    this._TdxApiIdInput.setAttribute("id", "widget-track-tdx-id");
    this._TdxApiIdInput.placeholder = "請至 TDX 官網註冊取得 API Client Id";

    this._TdxApiSecretInput = document.createElement("input");
    this._TdxApiSecretInput.setAttribute("type", "text");
    this._TdxApiSecretInput.setAttribute("id", "widget-track-tdx-secret");
    this._TdxApiSecretInput.placeholder =
      "請至 TDX 官網註冊取得 API Client Secret";

    this._TdxSelectType = document.createElement("select");
    this._TdxSelectType.classList.add("ov-widget-select");
    this._TDXApi.forEach((value) => {
      const op = document.createElement("option");
      op.text = value.name;
      this._TdxSelectType.append(op);
    });

    this._TdxOkBtn = document.createElement("button");
    this._TdxOkBtn.classList.add("ov-widget-btn");
    this._TdxOkBtn.innerText = "新增";

    this._TdxContainer.append(this._TdxSelectType);
    this._TdxContainer.append(this.divider.cloneNode(true));
    this._InsertContent(
      "Client ID",
      this._TdxApiIdInput,
      this._TdxContainer,
      false
    ).style.marginBottom = "0.2rem";
    this._InsertContent(
      "Client Secret",
      this._TdxApiSecretInput,
      this._TdxContainer
    );
    this._TdxContainer.append(this._TdxOkBtn);

    //#endregion TDX Tab

    const mainContainer = this._WidgetUI.getContentElement();
    mainContainer.classList.add("ov-widget-tabs");
    mainContainer.append(radio);
    mainContainer.append(label);
    mainContainer.append(this._ApiContainer);
    mainContainer.append(radio2);
    mainContainer.append(label2);
    mainContainer.append(this._FileContainer);
    mainContainer.append(radio3);
    mainContainer.append(label3);
    mainContainer.append(this._TdxContainer);
    mainContainer.append(this._ResultTabRadio);
    mainContainer.append(label4);
    mainContainer.append(resultContainer);
    
    // @ts-ignore
    window.eTagEntities = this._ETagEntities;
  }

  _InsertContent(
    label: string,
    content: HTMLElement,
    container: HTMLElement,
    withDivider: boolean = true
  ) {
    const innerContainer = document.createElement("div");
    innerContainer.classList.add("ov-widget-inputContainer");

    const labelContainer = document.createElement("div");
    labelContainer.classList.add("ov-widget-inputTitle");
    labelContainer.innerText = label;
    innerContainer.append(labelContainer);

    content.classList.add("ov-widget-inputItem");
    innerContainer.append(content);

    container.append(innerContainer);

    withDivider && container.append(this.divider.cloneNode(true));

    return innerContainer;
  }

  _initEvent() {
    this._OkBtn.addEventListener("click", (evt) => {
      if (!this._APIKeyInput.value) {
        ov.UI.Alertify.Error("請先輸入認證碼");
      } else if (this._FlightIdInput.value) {
        const request = new XMLHttpRequest();
        request.onreadystatechange = function (
          this: TrackWidget,
          xmlHttp: XMLHttpRequest
        ) {
          if (xmlHttp.readyState === 4) {
            if (xmlHttp.status === 200) {
              this._TerrainView.getModule("track").addFlightAwareEntity({
                source: JSON.parse(xmlHttp.responseText),
                callback: this._addEntityComplete.bind(
                  this,
				  "FlightAwareAPI",
                  this._FlightIdInput.value
                ),
              });
            } else {
              ov.UI.Alertify.Error("API Server 回應：" + xmlHttp.statusText);
            }
          }
        }.bind(this, request);
        try {
          request.open(
            "GET",
            this._Proxy +
              this._APIServer +
              "flights/" +
              this._FlightIdInput.value +
              "/track",
            true
          );
          request.setRequestHeader("Accept", "application/json;charset=UTF-8");
          request.setRequestHeader("x-apikey", this._APIKeyInput.value);
          request.send(null);
        } catch (e: any) {
          ov.UI.Alertify.Error(e.toString());
        }
        ov.UI.Alertify.Notify("向API請求資料中，請稍後...");
      } else {
        ov.UI.Alertify.Error("請先輸入Flight Id");
      }
    });

    this._UploadBtn.addEventListener("click", (evt) => {
		// const fileTypes = ["GPX", "FlightAware", "FlightRadarCSV", "AIS"];
      if (this._FileInput.files!.length) {
        const param = {
          onGround: false,
          source: this._FileInput.files![0],
          callback: this._addEntityComplete.bind(
            this,
			this._SelectType.options[this._SelectType.selectedIndex].text,
            this._FileInput.files![0].name
          ),
        };
        const mod = this._TerrainView.getModule("track");
        switch (this._SelectType.selectedIndex) {
          case 0:
            param.onGround = true;
            mod.addGPXEntity(param);
            break;
          case 1:
            mod.addFlightAwareEntity(param);
            break;
          case 2:
            mod.addFlightRadarEntity(param);
            break;
          case 3:
            mod.addAISEntity(param);
            break;
        }
      }
    });

    this._TdxOkBtn.addEventListener("click", (evt) => {

      if (!this._TdxApiSecretInput.value) {
        ov.UI.Alertify.Error("請先輸入 TDX APIKEY");
      } else {
        this._addTDXEntity(this._TdxSelectType.value);
      }
    });
  }

  _init() {
    const t = Math.ceil(new Date().getTime() * Math.random());
    this._HighwayLayer = this._TerrainView.addTerrainCustomTileOverlay({
      layername: "TrackWidget-Highway-" + t,
    })!;
    this._EtagHsinchuLayer = this._TerrainView.addCustomLayer({
      layername: "TrackWidget-Etag-Hsinchu-" + t,
    })!;
    this._EtagHsinchuCountyLayer = this._TerrainView.addCustomLayer({
      layername: "TrackWidget-Etag-HsinchuCounty-" + t,
    })!;

    // this._HighwayLayer = this._TerrainView.addTerrainCustomTileOverlay({
    //   layername: "TestCustomTile",
    // });
  }

  _addEntityComplete(type: string, info: string = "", result: string | any) {
    if (result) {      
      if (typeof result === "string") {
        ov.UI.Alertify.Error(result);
      } else if (
        result._entity._MarkEntities.length === 0 &&
        result._entity._PathEntities.length === 0 &&
        (!result.isAis || result.tracks.length === 0)
      ) {
        ov.UI.Alertify.Error("確認檔案內容與類型是否匹配");
      } else {
        const wrapper = document.createElement("div");
		wrapper.classList.add("ov-widget-trackWidget-resultRecord");

		const infoContainer = document.createElement("div");
		infoContainer.style.display = "flex";
		infoContainer.style.flexDirection = "column";
		infoContainer.style.flexGrow = "1";
		const titleEle = document.createElement("div");
		titleEle.style.textOverflow = "ellipsis";	
		titleEle.innerText = type;
		titleEle.setAttribute("title", titleEle.innerText);
		const infoEle = document.createElement("div");
		infoEle.style.textOverflow = "ellipsis";
		infoEle.style.fontSize = "0.8rem";	
		infoEle.innerText = info;
		infoEle.setAttribute("title", infoEle.innerText);
		
		infoContainer.append(titleEle);
		infoContainer.append(infoEle);

		const btnContainer = document.createElement("div");
		btnContainer.style.display = "flex";

        const locateBtn = document.createElement("button");
        locateBtn.classList.add("ov-widget-smallBtn");
        const showBtn = locateBtn.cloneNode(true) as HTMLButtonElement;
        const deleteBtn = locateBtn.cloneNode(true) as HTMLButtonElement;
        locateBtn.classList.add("ov-widget-locateBtn");
        locateBtn.setAttribute("title", "定位");
        showBtn.classList.add("ov-widget-showBtn");
        showBtn.classList.add("active");
        showBtn.setAttribute("title", "隱藏/顯示");
        deleteBtn.classList.add("ov-widget-deleteBtn");
        deleteBtn.setAttribute("title", "移除");
        const mod = this._TerrainView.getModule("track");

        locateBtn.addEventListener("click", (event) => {
          result.goto();
        });

        showBtn.addEventListener("click", (event) => {
          showBtn.classList.toggle("active");
          result.show = showBtn.classList.contains("active");
          this._TerrainView.updateScreen();
        });

        deleteBtn.addEventListener("click", (event) => {
          mod.removeEntity(result);
          if (result.isAis) {
            result.tracks.forEach((ent: any) => {
              mod.removeEntity(ent);
            });
          }

          if (this._Timeline) {
            this._Timeline.removeLink(result);
          }
          wrapper.remove();
          this._TerrainView.updateScreen();
        });

		btnContainer.append(showBtn);
		btnContainer.append(locateBtn);
		btnContainer.append(deleteBtn);
        
        const _title = info;

        // wrapper.append(_title);
        // wrapper.append(deleteBtn);
        // wrapper.append(locateBtn);
        // wrapper.append(showBtn);
        
        if (this._Timeline && result.playingInfo) {
          if (result.play !== null) {
            showBtn.classList.remove("active");
            this._Timeline.addLink(result, _title);
          }

          const block = this._TerrainView.addTimeEvent({
            start: result.playingInfo.TimeSpan[0],
            end: result.playingInfo.TimeSpan[1],
            onEnter: function () {
              showBtn.classList.add("active");
            },
            onLeave: function () {
              showBtn.classList.remove("active");
            },
          });

          deleteBtn.addEventListener("click", (event) => {
            this._TerrainView.removeTimeEvent(block);
          });

          const resetBtn = document.createElement("button");
          resetBtn.classList.add("ov-widget-smallBtn");
          resetBtn.classList.add("ov-widget-resetBtn");
          resetBtn.setAttribute("title", "回到起點");
          resetBtn.addEventListener("click", (event) => {
            this._Timeline!.updateTime(result.playingInfo.TimeSpan[0]);
          });
		
		  btnContainer.prepend(resetBtn);        

          const fastForwardBtn = document.createElement("button");
          fastForwardBtn.classList.add("ov-widget-smallBtn");
          fastForwardBtn.classList.add("ov-widget-fastForwardBtn");
          fastForwardBtn.setAttribute("title", "快轉至指定時間");
          fastForwardBtn.addEventListener("click", (event) => {
            const timeOffset = new Date().getTimezoneOffset() * 60000;
            const value = new Date(
              result.playingInfo.TimeSpan[0].getTime() - timeOffset
            )
              .toISOString()
              .slice(0, -1);
            new ov.UI.FormDialog(this._TerrainView, {
              form: {
                formContents: {
                  Time: {
                    type: "datetime-local",
                    value: value,
                  },
                },
                formTitle: {
                  icon: "fast_forward",
                  text: "指定時間",
                },
              },
              onOk: (value) => {
                this._Timeline!.updateTime(
                  new Date(
                    Math.min(
                      result.playingInfo.TimeSpan[1].getTime(),
                      Math.max(
                        result.playingInfo.TimeSpan[0].getTime(),
                        new Date(value.Time).getTime()
                      )
                    )
                  )
                );
              },
              mask: true,
            });
          });

		  btnContainer.prepend(fastForwardBtn);		
        }		
		
          if (result.isAis || type === "AIS") {
            const target = document.createElement("input");
            target.type = "text";
            target.classList.add("ov-widget-trackWidget-targetInput");
            target.setAttribute("placeholder", "輸入ID以過濾");
			infoEle.append(target);
            // wrapper.append(target);
            target.addEventListener("input", (evt) => {
              result.targetID = (evt.target as HTMLInputElement).value;
            });
          }

		wrapper.append(infoContainer);
		wrapper.append(btnContainer);

        this._ResultContainer.append(wrapper);
        ov.UI.Alertify.Notify("新增軌跡完成");
        this._ResultTabRadio.checked = true;
      }
    } else {
      ov.UI.Alertify.Error("Unknown failure!");
    }
  }

  //#region TDX

  async _addTDXEntity(param: string | any) {
    if (this._isTokenExpired() || this._isApiInfoChanged()) {
      await this._getTDXAuthorizationHeader();
    }

    const endpoint = this._TDXApi.find((api) => api.name === param)?.url;

    if (!endpoint) return;

    const result = await this._getTdxDataByAPI(endpoint);

    if (endpoint == "/v2/Road/Traffic/Live/Freeway") {
      this._highwayTrafficHandler(result);
    } else if (endpoint.includes("/v2/Road/Traffic/Live/ETag/City")) {
      this._etagTrafficHandler(result);
    }    
  }

  async _addTDXEntityCompleted(
    layer: ov.TerrainCustomTileOverlay | ov.CustomLayer | ov.Layer | null | undefined,
    type: string,
    timeTag: string = ""
  ) {

    const wrapper = document.createElement("div");
	  wrapper.classList.add("ov-widget-trackWidget-resultRecord");
    wrapper.classList.add("ov-widget-trackWidget-resultRecord-" + type);

	const infoContainer = document.createElement("div");
	infoContainer.style.display = "flex";
	infoContainer.style.flexDirection = "column";
	infoContainer.style.flexGrow = "1";
	const titleEle = document.createElement("div");
	titleEle.style.textOverflow = "ellipsis";	
	titleEle.innerText = (this._TDXApi.find((api) => api.type === type))?.shortName || ("TDX API 資料");
	titleEle.setAttribute("title", titleEle.innerText);
	const infoEle = document.createElement("div");
	infoEle.style.textOverflow = "ellipsis";
	infoEle.style.fontSize = "0.8rem";	
	infoEle.innerText = "資料時間：" + timeTag;
	infoEle.setAttribute("title", infoEle.innerText);

	infoContainer.append(titleEle);
	infoContainer.append(infoEle);

	const btnContainer = document.createElement("div");
	btnContainer.style.display = "flex";

    const locateBtn = document.createElement("button");
    locateBtn.classList.add("ov-widget-smallBtn");
    const showBtn = locateBtn.cloneNode(true) as HTMLButtonElement;
    const deleteBtn = locateBtn.cloneNode(true) as HTMLButtonElement;

    locateBtn.classList.add("ov-widget-locateBtn");
    locateBtn.setAttribute("title", "定位");
    showBtn.classList.add("ov-widget-showBtn");
    showBtn.classList.add("active");
    showBtn.setAttribute("title", "隱藏/顯示");
    deleteBtn.classList.add("ov-widget-deleteBtn");
    deleteBtn.setAttribute("title", "移除");

    locateBtn.addEventListener("click", (event) => {
      
      if (type === "highway") {        
        const point = new window.GeoPoint(120.818713, 23.5684329, 512358);
        this._TerrainView.goto({pos:point, distance: point.z});
      }
      else if (type.includes("etag")) {
        const city = type.substring(4);
        this.gotoCity(city);
      }
      
    });

    showBtn.addEventListener("click", (event) => {
      showBtn.classList.toggle("active");
      layer!.show = showBtn.classList.contains("active");
      this._TerrainView.updateScreen();
    });

    deleteBtn.addEventListener("click", (event) => {
      
      wrapper.remove();

      if (type === "highway") {
        this._TerrainView.removeTerrainOverlay(layer as ov.TerrainCustomTileOverlay);
        this._HighwayLayer = null;
        this._HighwayLegend!.remove();
        this._HighwayLegend = undefined;
      }
      else if (type.includes("etag")) {
        this._TerrainView.removeLayer(layer as ov.CustomLayer);
        const city = type.substring(4);
        this._ETagEntities[city].layer = undefined;

        var widgetUI = this._WidgetUI!.getElement();
        var dataItems = widgetUI.querySelectorAll(
          ".ov-widget-trackWidget-resultRecord"
        );        
        var eTagItemLenght = [...dataItems].filter((item) =>
          item.className.includes("etag")
        ).length;
        
        if (eTagItemLenght === 0) {
          this._EtagLegend?.remove();
          this._EtagLegend = undefined;
        }
      }
      
      this._TerrainView.updateScreen();
    });

	btnContainer.append(showBtn);
	btnContainer.append(locateBtn);
	btnContainer.append(deleteBtn);
    
    wrapper.append(infoContainer);
	wrapper.append(btnContainer);

    

    this._ResultContainer.append(wrapper);
    ov.UI.Alertify.Notify("新增即時路況完成");
    this._ResultTabRadio.checked = true;
  }

  gotoCity(city: string) {
    const cititesPosition: { [key: string]: GeoPoint } = {
      Taipei: new window.GeoPoint({"x":121.55571411919937,"y":25.076538645837857,"z":35196.06828161581}),
      NewTaipei: new window.GeoPoint({"x":121.55674245412945,"y":24.970004748848467,"z":90706.54013239434}),
      Taoyuan: new window.GeoPoint({"x":121.16142088123121,"y":24.863790162183534,"z":80152.81601481442}),
      Taichung: new window.GeoPoint({"x":120.73242754266391,"y":24.187736133348324,"z":57121.87407761267}),
      Tainan: new window.GeoPoint({"x":120.33967465172486,"y":23.1295209604139,"z":71061.09647388318}),
      Kaohsiung: new window.GeoPoint({"x":120.5658222506978,"y":22.881065943233963,"z":125833.24878102746}),
      Keelung: new window.GeoPoint({"x":121.71309970410005,"y":25.10758560415951,"z":16901.98980638795}),
      Hsinchu: new window.GeoPoint({"x":120.94449810418456,"y":24.782774324650546,"z":21288.56646152329}),
      HsinchuCounty: new window.GeoPoint({"x":121.0950454682906,"y":24.662176494606243,"z":67491.0829568951}),
      MiaoliCounty: new window.GeoPoint({"x":120.90024881668052,"y":24.48929283980776,"z":64884.72903635817}),
      ChanghuaCounty: new window.GeoPoint({"x":120.47236851868473,"y":23.99231062314138,"z":64884.72903635983}),
      NantouCounty: new window.GeoPoint({"x":120.9742122334003,"y":23.78227518170172,"z":113113.6950184827}),
      Chiayi: new window.GeoPoint({"x":120.4427433910497,"y":23.475170311077804,"z":12477.887233459978}),
      ChiayiCounty: new window.GeoPoint({"x":120.49047406157615,"y":23.40833025225507,"z":62299.633794021705}),
      YunlinCounty: new window.GeoPoint({"x":120.32741512021397,"y":23.674417499771096,"z":46933.15347744207}),
      PingtungCounty: new window.GeoPoint({"x":120.678169428371,"y":22.35493466682287,"z":136821.0295920161}),
      YilanCounty: new window.GeoPoint({"x":121.65365695733652,"y":24.605648134709625,"z":92610.12823027617}),
      HualienCounty: new window.GeoPoint({"x":121.3657998907533,"y":23.710136479106424,"z":181927.07925011948}),
      TaitungCounty: new window.GeoPoint({"x":121.07365275972768,"y":22.74051547603655,"z":145892.2731876692}),
      PenghuCounty: new window.GeoPoint({"x":119.59579454560077,"y":23.46206736869125,"z":86224.79585048351}),
      LienchiangCounty: new window.GeoPoint({"x":119.93474061745508,"y":26.122907549338308,"z":70051.03149966884}),
      KinmenCounty: new window.GeoPoint({"x":118.37139355990507,"y":24.44479356499255,"z":29285.89328477848}),
    }
    const point = cititesPosition[city];    
      this._TerrainView.goto({pos: point, distance: point.z});
    }

  _isTokenExpired() {
    if (!this._TDXCert?.token || !this._TDXCert?.token.timestamp) {
      return true;
    }
    const { token } = this._TDXCert;
    const { expires_in, timestamp } = token;
    const now = new Date().getTime();
    const diff = now - timestamp;
    return diff > expires_in * 1000; // expires_in 是秒，diff 是毫秒，需乘以1000
  }

  _isApiInfoChanged() {
    if (!this._TDXCert) return false;

    const { client_id, client_secret } = this._TDXCert;
    const client_id_input = this._TdxApiIdInput.value;
    const client_secret_input = this._TdxApiSecretInput.value;
    return (
      client_id !== client_id_input || client_secret !== client_secret_input
    );
  }

  async _getTDXAuthorizationHeader() {
    const client_id = this._TdxApiIdInput.value;
    const client_secret = this._TdxApiSecretInput.value;
    const grant_type = "client_credentials";
    const auth_url =
      "https://tdx.transportdata.tw/auth/realms/TDXConnect/protocol/openid-connect/token";

    this._TDXCert = {
      client_id,
      client_secret,
      grant_type,
    };

    const token = await fetch(auth_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        client_id,
        client_secret,
        grant_type,
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error("API request failed");
        }
      })
      .catch((error) => {
        console.error("Error:", error.message);
      });

    this._TDXCert.token = {
      ...token,
      timestamp: new Date().getTime(),
    };

    // console.log("已取得 access token: ", this._TDXCert.token);
  }

  async _getTdxDataByAPI(endpoint: string, query: any = {}) {
    if (!this._TDXCert?.token) return;

    const { access_token } = this._TDXCert?.token;

    const url = new URL(this._TDXServer + endpoint);
    url.search = new URLSearchParams(query).toString();

    const result = await fetch(url, {
      method: "GET",
      headers: {
        "Accept-Encoding": "br,gzip",
        "Content-Type": "application/json",
        authorization: "Bearer " + access_token,
      },
    })
      .then((response) => response.json())
      .then((data) => data)
      .catch((error) => {
        console.error("Error:", error.message);
      });

    return result;
  }

  //#endregion TDX

  //#region HighwayTraffic

  async _highwayTrafficHandler(data: any) {
	await this._highwaySectionDataUpdater();

    const traffics = data.LiveTraffics;
    const dataSrcUpdateTime = this._formatDateTime(data.SrcUpdateTime);

    if (this._HighwayLayer === undefined || this._HighwayLayer === null) {
      const t = Math.ceil(new Date().getTime() * Math.random());
      this._HighwayLayer = this._TerrainView.addTerrainCustomTileOverlay({
        layername: "TrackWidget-Highway-" + t,
      })!;
    }    

    traffics.forEach((traffic: any) => {     
	  const sectionId = traffic.SectionID;
      const speed = traffic.TravelSpeed;
      
      if (
        traffic.hasOwnProperty("OpenLRs") === false ||
        traffic.OpenLRs.length === 0
      ) {
        // console.log("無 OpenLR 資料");
        // console.log("traffic 資料: ", traffic);
      } else {
        traffic.OpenLRs.forEach((openLR: any) => {
          const lrp = OpenLRDecoder.decode(openLR.OpenLR);
          this._createPath(sectionId, speed, lrp);
        });
      }
    });

    if (
      document.querySelector(".ov-widget-trackWidget-resultRecord-highway") ===
      null
    ) {
      this._addTDXEntityCompleted(
        this._HighwayLayer!,
        "highway",
        dataSrcUpdateTime
      );
      this._createHighwayLegend();
    }
  }

  _getHighwayTrafficeLegendColor(
    speed: number | undefined
  ): string | undefined {
    if (speed == -2 || speed == undefined) return "#FFFFFF"; // 無資料
    if (speed == -1) return "#000000"; // 道路封閉
    if (speed >= 80) return "#00FF00";
    if (speed >= 60) return "#FFFF00";
    if (speed >= 40) return "#FFA500";
    if (speed >= 20) return "#FF0000";
    if (speed >= 0) return "#AA1EAA";
  }

  async _highwaySectionDataUpdater() {
    // 若已有資料則不再更新    
	if (this._HighwaySectionDataUpdated) { return; }

    // 檢查 Local Storage 是否有資料, 若有則檢查 SrcUpdate 的日期是否為今天, 若是則直接使用
    const localData = localStorage.getItem("highwaySectionData");

    if (localData) {
      const data = JSON.parse(localData);
      const today = new Date();
      const srcUpdate = new Date(data.SrcUpdateTime);
      if (
        today.getDate() === srcUpdate.getDate() &&
        today.getMonth() === srcUpdate.getMonth() &&
        today.getFullYear() === srcUpdate.getFullYear()
      ) {
		    this._HighwaySectionData = data;
        this._HighwaySectionDataUpdated = true;        
        return;
      }
    }

    // 若無資料則直接更新
    const endpoint = "/v2/Road/Traffic/Section/Freeway";
    const data = await this._getTdxDataByAPI(endpoint);
    this._HighwaySectionData = data;
    localStorage.setItem("highwaySectionData", JSON.stringify(data));
    this._HighwaySectionDataUpdated = true;
  }

  _getHighwaySectionData(sectionId: string) {
	return this._HighwaySectionData?.Sections.find(
	  (section: any) => section.SectionID === sectionId
	);
  }

  _createPath(sectionId: string, speed: number, decodedOepnLR: any, ) {
	const color = this._getHighwayTrafficeLegendColor(speed);
	const sectionData = this._getHighwaySectionData(sectionId);
	const tooltipDescription = this._getSectionTooltipDescription(sectionData, speed);

    const points = [];
    points.push(
      new window.GeoPoint(
        decodedOepnLR.StartLocation.PositionLon,
        decodedOepnLR.StartLocation.PositionLat
      )
    );
    decodedOepnLR.MidLocation.forEach((lrp: any) => {
      points.push(new window.GeoPoint(lrp.PositionLon, lrp.PositionLat));
    });
    points.push(
      new window.GeoPoint(
        decodedOepnLR.EndLocation.PositionLon,
        decodedOepnLR.EndLocation.PositionLat
      )
    );

    const entity = this._HighwayLayer?.addPolylineEntity({
      geo: new window.GeoPolyline(points),
      lineWidth: 5,
      strokeStyle: color as ov.CSSColor || "#FFFFFF" as ov.CSSColor,
      visibleLevel: 0,
      // todo: 等 TerrainOverlay 支援 tooltip 或點擊事件後再啟用
	    //   onClick: this._setHightwaySectionTooltip.bind(this, tooltipDescription),
      //   或者
	    //   onClick: (mousePos: GeoPoint) => {
		  //    console.log("mousePos", mousePos);
		  //    this._setHightwaySectionTooltip(tooltipDescription, mousePos);
	    //   }
    });

    this._TerrainView.updateScreen();
  }

  _getSectionTooltipDescription(sectionData: any, speed: number | undefined) {
	const directionSymbol: { [key: string]: string } = {
		"S": "南向",
		"N": "北向",
		"E": "東向",
		"W": "西向",
	}
	const name = sectionData?.SectionName;
	const direction: string = sectionData?.Direction;
	const speedLimit = sectionData?.SpeedLimit;
	const startKm = sectionData?.StartKm;
	const endKm = sectionData?.EndKm;
	const description = `<div class="widgetTrackTooltip">
	  <div>${name} ${directionSymbol[direction]}</div>
	  <div>${startKm} - ${endKm} 路段</div>
	  <div>限速: ${speedLimit} 公里</div>
	  <div>目前速度: ${speed ? speed : "No Data"} km/h</div>
	  </div>
	  `
	return description;
  }

  _setHightwaySectionTooltip(description: string, mousePos: GeoPoint) {
	
	const tooltipEle: HTMLElement | null = document.querySelector(".EntityTooltip");	
	if (tooltipEle) {	  
	  tooltipEle.style.left = mousePos.x + 10 + "px";
	  tooltipEle.style.top = mousePos.y + 10 + "px";
	  tooltipEle && (tooltipEle.innerHTML = description);
	  tooltipEle.style.opacity = "1";	  
	}	
  }

  _createHighwayLegend() {
    
    const wrapper = this._createLegendWrapper();
    wrapper.style.flexDirection = "column";
    wrapper.style.maxWidth = "200px";
	if (this._EtagLegend) wrapper.style.bottom = "7%";

    const trafficLegendContainer = this._createLegendContainer();

    const lblStart = this._createLegendTextBlock("順暢");
    trafficLegendContainer.append(lblStart);

    const colors = ["#00FF00", "#FFFF00", "#FFA500", "#FF0000", "#aa1eaa"];
    colors.forEach((color, index) => {
      const item = this._createLegendColorBlock(color);
      trafficLegendContainer.append(item);
    });

    const lblEnd = this._createLegendTextBlock("擁塞");
    trafficLegendContainer.append(lblEnd);

    wrapper.append(trafficLegendContainer);

    const subLegendContainer = this._createLegendContainer();
    subLegendContainer.style.marginTop = "0.1rem";
    subLegendContainer.style.justifyContent = "space-evenly";
    const colorItemRoadClose = this._createLegendColorBlock("#000000");
    const lblRoadClose = this._createLegendTextBlock("道路封閉");
    const colorItemDataLoss = this._createLegendColorBlock("#FFFFFF");
    const lblDataLoss = this._createLegendTextBlock("資料遺失");
    subLegendContainer.append(colorItemRoadClose);
    subLegendContainer.append(lblRoadClose);
    subLegendContainer.append(colorItemDataLoss);
    subLegendContainer.append(lblDataLoss);

    wrapper.append(subLegendContainer);

	const titleContainer = this._createLegendContainer();
	titleContainer.style.justifyContent = "center";
	titleContainer.style.borderTop = "1px solid gray";
	titleContainer.style.borderRadius = "0px";
	titleContainer.style.marginTop = "2px";
	titleContainer.style.fontSize = "12px";
	titleContainer.style.color = "slategray";
	const title = this._createLegendTextBlock("高速公路即時路況 圖例");
	titleContainer.append(title);

	wrapper.append(titleContainer);

    this._HighwayLegend = wrapper;
    this._setContainerDraggable(this._HighwayLegend);
    document.body.append(this._HighwayLegend);
  }

  //#endregion HighwayTraffic

  //#region EtagTraffic
  async _etagTrafficHandler(data: any) {
    const ETagPairLives = data.ETagPairLives;
    const AuthorityCode: string = data.AuthorityCode;
    const CityNameEng: string | undefined = TDXCityInfo.getCityInfoByCityCode(AuthorityCode)?.CityNameEng;
    const dataSrcUpdateTime = this._formatDateTime(data.SrcUpdateTime);

    let eTagCityEntity;

    if (CityNameEng && this._ETagEntities.hasOwnProperty(CityNameEng) === false) {
      const t = Math.ceil(new Date().getTime() * Math.random());
      this._ETagEntities[CityNameEng] = {
        layer: this._TerrainView.addCustomLayer({
          layername: `TrackWidget-Etag-${CityNameEng}-${t}`,
        })!,
      };      
    }
    
    eTagCityEntity = this._ETagEntities[CityNameEng!];


    if (CityNameEng !== undefined) {
      await this._etagPairsDataUpdater(CityNameEng);

      // const ETagPairsData = this[`_EtagPairsData${CityNameEng}`];
      // const layer = this[`_Etag${CityNameEng}Layer`];

      const ETagPairsData = eTagCityEntity.pairData;
      const layer = eTagCityEntity.layer;

      ETagPairLives.forEach((ETagPairLive: any) => {
        const targetEtagPair = ETagPairsData.ETagPairs.find(
          (etagPair: any) => etagPair.ETagPairID === ETagPairLive.ETagPairID
        );

        if (targetEtagPair === undefined) {
          // console.log("無 ETagPair 資料");
          // console.log("traffic 資料: ", ETagPairLive);
        } else {          
          const pathGeometry = targetEtagPair.Geometry;
          const speed =
            ETagPairLive.Flows.reduce(
              (acc: any, flow: any) => acc + (flow.SpaceMeanSpeed == -99 ? 0 : flow.SpaceMeanSpeed),
              0
            ) / ETagPairLive.Flows.filter((flow:any)=> flow.SpaceMeanSpeed !== -99).length;
          // console.log(`正在處理 ETagPair: ID-${targetEtagPair.ETagPairID}, ${targetEtagPair.Description}` + ` 速度: ${speed}` );
          const color: string = this._getRoadTrafficColor(speed)!;
          this._createGeometryPath(
            pathGeometry,
            color,
            speed,
            targetEtagPair.Description,
            layer!,
            ETagPairLive
          );
        }
      });
    }

    // const logText = `${CityNameEng} - ETagPairLives 資料筆數: ${(ETagPairLives.length+"").padStart(3,'0')}, ETagPairsData 資料筆數: ${(this._ETagEntities[CityNameEng!]?.pairData.ETagPairs.length+"").padStart(3,'0')}`;

    // console.log(logText);
    // window.navigator.clipboard.writeText(logText);
    // if (!window.hasOwnProperty("ETagCityData")) {
    //   //@ts-ignore
    //   window.ETagCityData = [];
    // }
    // //@ts-ignore
    // window.ETagCityData.push(logText);

    if (
      document.querySelector(
        ".ov-widget-trackWidget-resultRecord-etag" + CityNameEng
      ) === null
    ) {
      this._addTDXEntityCompleted(
        eTagCityEntity.layer,
        "etag" + CityNameEng,
        dataSrcUpdateTime
      );
      this._EtagLegend === undefined && this._createEtagLegend();
    }
  }

  async _etagPairsDataUpdater(city: string) {
    // 若已有資料則不再更新
    const eTagCityEntity = this._ETagEntities[city];
    if (eTagCityEntity && eTagCityEntity.pairDataUpdated) {
      // console.log(`${city} 已有 EtagPairs 資料`);
      return;
    }


    // 檢查 Local Storage 是否有資料, 若有則檢查 SrcUpdate 的日期是否為今天, 若是則直接使用
    const localData = localStorage.getItem("etagPairsData" + city);

    if (localData) {
      const data = JSON.parse(localData);
      const today = new Date();
      const srcUpdate = new Date(data.SrcUpdateTime);
      if (
        today.getDate() === srcUpdate.getDate() &&
        today.getMonth() === srcUpdate.getMonth() &&
        today.getFullYear() === srcUpdate.getFullYear()
      ) {
        this._ETagEntities[city].pairData = data;
        this._ETagEntities[city].pairDataUpdated = true;

        // console.log(`使用本地端儲存的 ${city} EtagPairs 資料`);
        return;
      }
    }

    // 若無資料則直接更新

    const endpoint = "/v2/Road/Traffic/ETagPair/City/" + city;
    const data = await this._getTdxDataByAPI(endpoint);

    this._ETagEntities[city].pairData = data;
    this._ETagEntities[city].pairDataUpdated = true;
    localStorage.setItem("etagPairsData" + city, JSON.stringify(data));


    // console.log(`已更新 ${city} EtagPairs 資料`);
  }

  _createGeometryPath(
    geometry: string,
    color: string,
    speed: number,
    description: string,
    layer: ov.CustomLayer,
    ETagPairLive: any
  ) {
    // geometry ex: ""LINESTRING(121.02799 24.81966,121.02666 24.81802,121.02592 24.81718)"
    // get points
    const points = geometry!.match(/\d+\.\d+\s\d+\.\d+/g)?.map((point) => {
      const [lon, lat] = point.split(" ");
      return new window.GeoPoint(parseFloat(lon), parseFloat(lat));    
    });

    let groundPolyline;
    const param = {
      geo: new window.GeoPolyline(points),
      color: new ov.Color(color),
      size: 5,
      opacity: 1.0,
      epsg: 4326,
      tooltip: `<div class="widgetTrackTooltip">
	  <div>${description}</div>
	  <div>速度: ${speed ? speed.toFixed(1) : "No Data"} km/h</div>
	  </div>
	  `,
      // onClick: (point: GeoPoint, parameter: any, sn?: number, name?: string) => {
      //   console.log("ETagPairLive", ETagPairLive);
      //   console.log("layer", layer);
      //   console.log("entity", groundPolyline!);
      //   ETagPairLive.Flows.forEach((flow: any) => {
      //     console.log(flow);
      //   })
      // }
    };

    groundPolyline = layer.addGroundPolylineEntity(param);
  }

  _getRoadTrafficColor(speed: number) {
    if (speed >= 50) return "#00FF00";
    if (speed >= 25) return "#FFFF00";
    if (speed >= 0) return "#FF0000";
    if (speed == -99) return "#000000"; // 資料異常
  }

  _createEtagLegend() {    
    const wrapper = this._createLegendWrapper();
	wrapper.style.flexDirection = "column";
	if (this._HighwayLegend) wrapper.style.bottom = "9%";

    const legendContainer = this._createLegendContainer();

    const lblStart = this._createLegendTextBlock("順暢");
    legendContainer.append(lblStart);

    const colors = ["#00FF00", "#FFFF00", "#FF0000"];
    colors.forEach((color, index) => {
      const item = this._createLegendColorBlock(color);
      legendContainer.append(item);
    });

    const lblEnd = this._createLegendTextBlock("封閉 | ");
    const colorItemError = this._createLegendColorBlock("#000000");
    const textItemError = this._createLegendTextBlock("資料異常");
    legendContainer.append(lblEnd);
    legendContainer.append(colorItemError);
    legendContainer.append(textItemError);

    wrapper.append(legendContainer);

	const titleContainer = this._createLegendContainer();
	titleContainer.style.justifyContent = "center";
	titleContainer.style.borderTop = "1px solid gray";
	titleContainer.style.borderRadius = "0px";
	titleContainer.style.marginTop = "2px";
	titleContainer.style.fontSize = "12px";
	titleContainer.style.color = "slategray";
	const title = this._createLegendTextBlock("ETag 即時路況 圖例");	
	titleContainer.append(title);

	wrapper.append(titleContainer);

    this._EtagLegend = wrapper;
    this._setContainerDraggable(this._EtagLegend);
    document.body.append(this._EtagLegend);
  }

  //#endregion EtagTraffic

  //#region UI-Helper
  _setContainerDraggable(container: HTMLElement) {
    let isDragging = false;
    let relativeX = 0;
    let relativeY = 0;
    let cursorTemp = container.style.cursor;

    container.addEventListener("mousedown", (event) => {
      isDragging = true;
      relativeX = event.clientX - container.offsetLeft;
      relativeY = event.clientY - container.offsetTop;
      container.style.cursor = "grabbing";
      event.preventDefault();
    });

    document.addEventListener("mousemove", (event) => {
      if (isDragging) {
        container.style.bottom = "auto";
        container.style.left = event.clientX - relativeX + "px";
        container.style.top = event.clientY - relativeY + "px";
      }
    });

    document.addEventListener("mouseup", () => {
      isDragging = false;
      container.style.cursor = cursorTemp;
    });
  }

  _createLegendColorBlock(color: string) {
    const item = document.createElement("div");
    item.style.display = "flex";
    item.style.alignItems = "center";
    const block = document.createElement("div");
    block.style.width = "20px";
    block.style.height = "20px";
    block.style.borderRadius = "3px";
    block.style.backgroundColor = color;
    return block;
  }

  _createLegendTextBlock(text: string) {
    const item = document.createElement("div");
    item.style.display = "flex";
    item.style.alignItems = "center";
    const span = document.createElement("span");
    span.innerText = text;
    item.append(span);
    return item;
  }

  _createLegendContainer() {
    const container = document.createElement("div");
    container.style.display = "flex";
    container.style.justifyContent = "space-between";
    container.style.alignItems = "center";
    container.style.borderRadius = "5px";
    container.style.width = "100%";
    return container;
  }

  _createLegendWrapper() {
    const wrapper = document.createElement("div");
    wrapper.classList.add("ov-widget-inputContainer");
    wrapper.style.position = "absolute";
    wrapper.style.bottom = "1%";
    wrapper.style.left = "10px";
    // wrapper.style.transform = "translate(-50%, -50%)";
    wrapper.style.backgroundColor = "white";
    wrapper.style.padding = "0.2rem";
    wrapper.style.zIndex = "1000";
    wrapper.style.border = "1px solid dimgray";
    wrapper.style.borderRadius = "5px";
    wrapper.style.boxShadow = "0 0 10px rgba(0, 0, 0, 0.5)";
    wrapper.style.backgroundColor = "var(--primary-100)";
    wrapper.style.maxWidth = "235px";
    wrapper.style.cursor = "grab";
    return wrapper;
  }

  //#region Utils
  _formatDateTime(isoString: string) {
    // 創建一個新的 Date 對象
    const date = new Date(isoString);

    // 獲取年份、月份和日期
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // 月份是0-indexed的，需要加1
    const day = String(date.getDate()).padStart(2, "0");

    // 獲取小時和分鐘
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    // 組合成所需的格式
    const formattedDateTime = `${year}-${month}-${day}_${hours}${minutes}`;

    return formattedDateTime;
  }
}

// @ts-ignore 硬塞進 Widget
ov.Widget.TrackWidget = TrackWidget;
